import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { ResourceStatus } from '@/lib/shared-types';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useDebounced } from '../lib/helpers';
import { meterFor, useMeterTick, type ResourceRow, type SessionRow } from '../lib/sessions';
import { useReducedMotion } from '../lib/useReducedMotion';
import type { ResourceBoard as Board } from '../lib/useResourceBoard';
import { ResourceCard } from './ResourceCard';
import type { ResourceAction } from './ResourceDialogs';

interface Props {
  open: boolean;
  board: Board;
  /**
   * One resource only — what a tap on the map asked for. The filters and the
   * summary tiles are about the whole floor, so they are not shown for one.
   */
  focusResourceId: string | null;
  /**
   * Resources drawn on some floor map. Everything else is reachable *only*
   * from here, and is labelled so, because a venue that never drew its PS5
   * room must not lose access to it.
   */
  placedResourceIds: Set<string>;
  onAction: (action: ResourceAction, resource: ResourceRow, session: SessionRow | null) => void;
  /** C-16 — open the create form. No resource yet, so it is not an action. */
  onNew: () => void;
  onClose: () => void;
}

/**
 * §10 — the resource floor, folded into the map view it belongs to.
 *
 * This is what the separate `/resources` page was for, and everything it did
 * that was worth keeping is here: the live meters, the running-total tiles, the
 * kids waiting list and the occupancy counts. What it is *not* any more is a
 * destination — the map is where a manager already is, so this opens over it,
 * either on one resource (a tap on its shape) or on all of them.
 *
 * The clock ticks only while this is open (`useMeterTick`), so the map behind
 * it does not repaint once a second for a panel nobody is looking at. Nothing
 * polls: the server is re-read when a `resource_session.*` event lands.
 */
export function ResourceBoard({
  open,
  board,
  focusResourceId,
  placedResourceIds,
  onAction,
  onNew,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  // I-13 — only the fallback: each resource and each meter names its own
  // currency, resolved server-side from its branch, and this panel can list
  // two branches at once.
  const { code: currencyCode } = useCurrency();
  const reducedMotion = useReducedMotion();

  const [areaId, setAreaId] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);

  const running = board.sessions.some((session) => session.status === 'ACTIVE');
  const now = useMeterTick(open && running);

  const focused = focusResourceId
    ? (board.resources.find((row) => row.id === focusResourceId) ?? null)
    : null;

  /** Areas come off the rows already in hand — no second request for a filter. */
  const areas = useMemo(() => {
    const map = new Map<string, string>();
    for (const resource of board.resources) {
      if (resource.area) map.set(resource.area.id, resource.area.name);
    }
    return [...map].sort((a, b) => a[1].localeCompare(b[1]));
  }, [board.resources]);

  /**
   * Filtering is local. The whole list is already here (one request, 200 rows),
   * and a round trip per keystroke was the wait on a weak link.
   */
  const visible = useMemo(() => {
    if (focused) return [focused];
    const term = debouncedSearch.trim().toLocaleLowerCase();
    return board.resources.filter((resource) => {
      if (areaId && resource.area?.id !== areaId) return false;
      if (status && resource.status !== status) return false;
      if (!term) return true;
      return (
        resource.name.toLocaleLowerCase().includes(term) ||
        (resource.code ?? '').toLocaleLowerCase().includes(term)
      );
    });
  }, [areaId, board.resources, debouncedSearch, focused, status]);

  const grouped = useMemo(() => {
    const groups = new Map<string, { name: string; rows: ResourceRow[] }>();
    for (const resource of visible) {
      const key = resource.area?.id ?? 'unknown';
      const group = groups.get(key) ?? {
        name: resource.area?.name ?? t('spaces.resources.noArea'),
        rows: [],
      };
      group.rows.push(resource);
      groups.set(key, group);
    }
    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [t, visible]);

  /**
   * The running total is only a number when every live meter bills in the same
   * currency. This venue can run two branches, and adding a dinar to a riyal
   * produces a figure that is wrong in both — so a mixed floor reports one
   * subtotal per currency instead of a single invented sum.
   */
  const runningTotals = useMemo(() => {
    const byCurrency = new Map<string, number>();
    for (const session of board.sessions) {
      const code = session.currency ?? currencyCode;
      byCurrency.set(code, (byCurrency.get(code) ?? 0) + (meterFor(session, now).amount ?? 0));
    }
    return [...byCurrency];
  }, [board.sessions, currencyCode, now]);

  const counts = {
    inUse: board.sessions.filter((session) => session.status === 'ACTIVE').length,
    paused: board.sessions.filter((session) => session.status === 'PAUSED').length,
    available: board.resources.filter(
      (resource) =>
        resource.status !== 'MAINTENANCE' &&
        resource.status !== 'OUT_OF_SERVICE' &&
        (board.sessionsByResource.get(resource.id)?.length ?? 0) <
          Math.max(1, resource.capacity),
    ).length,
  };

  const offMapCount = board.resources.filter((row) => !placedResourceIds.has(row.id)).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={focused ? focused.name : t('nav.resources')}
      description={focused ? undefined : t('spaces.resources.subtitle')}
      className="max-w-5xl"
    >
      {/* C-16 — the only place a resource can be created. Withheld rather than
          shown-and-refused when the operator lacks `resources.create`. */}
      {!focused && can('resources.create') && (
        <div className="mb-4 flex justify-end">
          <Button size="lg" onClick={onNew}>
            <Plus className="h-4 w-4" />
            {t('spaces.resourceForm.newTitle')}
          </Button>
        </div>
      )}

      {board.loading && board.resources.length === 0 ? (
        <LoadingState label={t('common.loading')} />
      ) : board.error ? (
        <ErrorState message={board.error} onRetry={board.reload} />
      ) : board.resources.length === 0 ? (
        <EmptyState title={t('spaces.resources.empty')} hint={t('spaces.resources.emptyHint')} />
      ) : (
        <div className="space-y-4">
          {!focused && (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { label: t('spaces.resources.summary.inUse'), value: String(counts.inUse) },
                  { label: t('spaces.resources.summary.paused'), value: String(counts.paused) },
                  {
                    label: t('spaces.resources.summary.available'),
                    value: String(counts.available),
                  },
                  {
                    label: t('spaces.resources.summary.running'),
                    value:
                      runningTotals.length === 0
                        ? formatCurrency(0, currencyCode, i18n.language)
                        : runningTotals
                            .map(([code, amount]) =>
                              formatCurrency(amount, code, i18n.language),
                            )
                            .join(' · '),
                  },
                ].map((tile) => (
                  <Card key={tile.label}>
                    <CardContent className="p-4">
                      <p className="text-xs text-ink-muted">{tile.label}</p>
                      <p className="mt-1 text-xl font-semibold tabular-nums">{tile.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <Field label={t('spaces.resources.filters.area')}>
                  <Select value={areaId} onChange={(event) => setAreaId(event.currentTarget.value)}>
                    <option value="">{t('spaces.resources.filters.allAreas')}</option>
                    {areas.map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label={t('spaces.resources.filters.status')}>
                  <Select value={status} onChange={(event) => setStatus(event.currentTarget.value)}>
                    <option value="">{t('spaces.resources.filters.allStatuses')}</option>
                    {Object.values(ResourceStatus).map((value) => (
                      <option key={value} value={value}>
                        {t(`spaces.resources.status.${value}`, value)}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label={t('common.search')}>
                  <div className="relative">
                    <Search className="pointer-events-none absolute start-3 top-3.5 h-4 w-4 text-ink-muted" />
                    <Input
                      value={search}
                      onChange={(event) => setSearch(event.currentTarget.value)}
                      placeholder={t('spaces.resources.searchPlaceholder')}
                      aria-label={t('common.search')}
                    />
                  </div>
                </Field>
              </div>

              {/* A resource nobody ever drew on a map is reachable only here,
                  and says so — otherwise a venue with an undrawn PS5 room would
                  have no way in at all. */}
              {offMapCount > 0 && (
                <p className="text-xs text-ink-muted">
                  {t('spaces.resources.offMapHint', { count: offMapCount })}
                </p>
              )}
            </>
          )}

          {visible.length === 0 ? (
            <EmptyState title={t('spaces.resources.empty')} hint={t('spaces.resources.emptyHint')} />
          ) : (
            grouped.map((group) => (
              <section key={group.name} className="space-y-2">
                {!focused && (
                  <h3 className="text-sm font-semibold text-ink-muted">
                    {group.name} · {t('spaces.resources.count', { count: group.rows.length })}
                  </h3>
                )}
                <div className={focused ? '' : 'grid gap-3 lg:grid-cols-2'}>
                  {group.rows.map((resource) => (
                    <div key={resource.id} className="space-y-1">
                      {!placedResourceIds.has(resource.id) && (
                        <Badge tone="neutral">{t('spaces.resources.offMap')}</Badge>
                      )}
                      <ResourceCard
                        resource={resource}
                        sessions={board.sessionsByResource.get(resource.id) ?? []}
                        now={now}
                        currency={resource.currency ?? currencyCode}
                        reducedMotion={reducedMotion}
                        busy={board.busyId === resource.id}
                        onStart={() => onAction('start', resource, null)}
                        onPause={(session) => onAction('pause', resource, session)}
                        onResume={(session) => onAction('resume', resource, session)}
                        onExtend={(session) => onAction('extend', resource, session)}
                        onStop={(session) => onAction('stop', resource, session)}
                        onCheckOutByTag={() => onAction('checkOutByTag', resource, null)}
                        onOrder={(session) => onAction('order', resource, session)}
                        onEdit={() => onAction('edit', resource, null)}
                        onRetire={() => onAction('retire', resource, null)}
                        onPricing={() => onAction('pricing', resource, null)}
                        onRefresh={board.reload}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      )}
    </Dialog>
  );
}
